import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { priorities, aboutSection, newsSection, coreValues, anniversary } from '../../mock';
import { useLang } from '../../i18n/LanguageContext';

export const PrioritiesBand = () => {
  const { t, tv } = useLang();
  return (
    <section className="relative bg-[#0f2340] text-white overflow-hidden">
      <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${priorities.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0a1929] via-[#0f2340]/90 to-[#0a1929]" />
      <div className="relative max-w-6xl mx-auto px-6 py-16 md:py-20 text-center">
        <div className="inline-block mb-6">
          <div className="h-[2px] w-24 bg-[#d4a017] mx-auto mb-3" />
          <span className="text-[#d4a017] text-xs tracking-[0.35em] font-semibold">{t('ourPriorities')}</span>
          <div className="h-[2px] w-24 bg-[#d4a017] mx-auto mt-3" />
        </div>
        <p className="text-xl md:text-2xl leading-relaxed font-light" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>
          {tv(priorities.headline)}
        </p>
      </div>
    </section>
  );
};

const FeatureCard = ({ data }) => {
  const { tv, t } = useLang();
  return (
    <Link to={data.href} className="group relative block overflow-hidden h-[380px] md:h-[440px]">
      <img src={data.image} alt={tv(data.title)} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a1929] via-[#0a1929]/40 to-transparent" />
      <div className="absolute inset-0 border-b-4 border-transparent group-hover:border-[#d4a017] transition" />
      <div className="absolute bottom-0 left-0 right-0 p-8">
        <h3 className="text-white text-4xl md:text-5xl font-bold tracking-wide mb-4" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{tv(data.title)}</h3>
        <span className="inline-flex items-center gap-2 text-[#d4a017] font-semibold text-sm tracking-widest uppercase group-hover:gap-4 transition-all">
          {t('learnMore')} <ArrowRight size={16} />
        </span>
      </div>
    </Link>
  );
};

export const AboutNews = () => (
  <section className="bg-white py-12">
    <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-6">
      <FeatureCard data={aboutSection} />
      <FeatureCard data={newsSection} />
    </div>
  </section>
);

export const CoreValuesBlock = () => {
  const { tv } = useLang();
  return (
    <section className="bg-[#0a1929] text-white py-20 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 20% 30%, #d4a017 0%, transparent 40%), radial-gradient(circle at 80% 70%, #d4a017 0%, transparent 40%)' }} />
      <div className="relative max-w-4xl mx-auto px-4 text-center">
        <div className="h-[3px] w-16 bg-[#d4a017] mx-auto mb-6" />
        <h2 className="text-5xl md:text-6xl font-bold mb-8 tracking-wide" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{tv(coreValues.title)}</h2>
        <div className="space-y-5 text-white/80 text-base md:text-lg leading-relaxed mb-8">
          {coreValues.body.map((p, i) => <p key={i}>{tv(p)}</p>)}
        </div>
        <Link to={coreValues.cta.href} className="inline-flex items-center gap-3 border-2 border-[#d4a017] text-[#d4a017] hover:bg-[#d4a017] hover:text-[#0a1929] font-bold px-8 py-3 tracking-widest uppercase text-sm transition">
          {tv(coreValues.cta.label)} <ArrowRight size={16} />
        </Link>
      </div>
    </section>
  );
};

export const AnniversaryBlock = () => {
  const { tv } = useLang();
  return (
    <section className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-2 gap-10 items-center">
        <div className="relative">
          <img src={anniversary.image} alt="" className="w-full h-[420px] object-cover shadow-2xl" />
          <div className="absolute -bottom-4 -right-4 bg-[#d4a017] text-[#0a1929] w-32 h-32 grid place-items-center shadow-lg" style={{ clipPath: 'polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0 75%, 0 25%)' }}>
            <div className="text-center">
              <div className="text-2xl font-bold leading-none">MoU</div>
              <div className="text-[10px] tracking-widest font-bold mt-1">KEMHAN</div>
            </div>
          </div>
        </div>
        <div>
          <div className="h-[3px] w-16 bg-[#d4a017] mb-4" />
          <div className="text-[#d4a017] text-xs font-bold tracking-[0.3em] uppercase mb-3">Kemitraan Strategis</div>
          <h2 className="text-4xl md:text-5xl font-bold text-[#0a1929] mb-6 tracking-wide" style={{ fontFamily: "'Roboto Condensed', sans-serif" }}>{tv(anniversary.title)}</h2>
          {anniversary.body.map((p, i) => (
            <p key={i} className="text-gray-700 text-base leading-relaxed mb-4">{tv(p)}</p>
          ))}
          <Link to={anniversary.cta.href} className="inline-flex items-center gap-2 text-[#0a1929] hover:text-[#d4a017] font-bold tracking-widest text-sm mt-2 group">
            {tv(anniversary.cta.label)}
            <span className="w-8 h-[2px] bg-current group-hover:w-12 transition-all" />
            <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
};
